import { axisTop } from "d3";
import { type FC, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { formatNumberByThousands } from "widgets/utils";

import style from "./styles/GenomeViewerAxis.module.scss";

import { rangeNormalize } from "@/app-utils/range.ts";
import { type Range } from "@/graphql.ts";
import { type IGenomeViewerStateLayout } from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { AXIS_HEIGHT, AXIS_TICK_COUNT, STANDARD_AXIS_TICK_HEIGHT } from "@/settings/settings";

const GenomeViewerAxis: FC = () => {
  const layout = useSelector<IAppState, IGenomeViewerStateLayout>((state) => state.genomeBrowser.layout, shallowEqual);
  const range = useSelector<IAppState, Range>((state) => state.genomeBrowser.range);
  const normalize = useMemo(() => rangeNormalize(layout.trackWidth)(range), [range, layout.trackWidth]);
  const axis = useMemo(
    () =>
      axisTop(normalize).ticks(AXIS_TICK_COUNT).tickSize(STANDARD_AXIS_TICK_HEIGHT).tickFormat(formatNumberByThousands),
    [normalize],
  );
  const height = useMemo(() => layout.viewHeight + AXIS_HEIGHT + 1, [layout.viewHeight]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const ticks = axis.scale().ticks();

  return (
    <div className={style.axis_container}>
      <svg width={layout.viewWidth} height={height} style={{ pointerEvents: "none" }}>
        {/* Let's fix blurry rendering */}
        <g transform={"translate(0.5, 0.5)"}>
          <g transform={`translate(${layout.headerWidth ?? 0})`}>
            {/* add the tick lines */}
            {ticks.map((tick, index) => (
              <g
                key={`tickline-${tick}-${index}`}
                transform={`translate(${Math.round(normalize(tick))}, ${Math.floor(AXIS_HEIGHT / 2 + 2)})`}
              >
                <text x={0} y={0} textAnchor={"middle"} className={style.text}>
                  {tick === 0 ? "" : formatNumberByThousands(tick)}
                </text>
                <line stroke={"#B6BABF"} strokeWidth={1} strokeDasharray={"2 3"} x1={0} y1={5} x2={0} y2={height} />
              </g>
            ))}
          </g>
        </g>
      </svg>
    </div>
  );
};

export { GenomeViewerAxis };
